import { useBranding } from '../hooks/useBranding';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Canonical brand icon size - MUST be consistent across all headers
// 32px = compact size for headers
const BRAND_ICON_SIZE = 32;

/**
 * BrandIcon Component - CONSISTENT SIZE EVERYWHERE
 * Uses a fixed-size container to prevent layout shifts
 * This component should be used in ALL headers across the app
 */
export const BrandIcon = () => {
  const { branding } = useBranding();
  const _sep = (branding.iconUrl || '').includes('?') ? '&' : '?';
  const cacheBust = branding._ts ? `${_sep}v=${branding._ts}` : '';
  
  return (
    <div 
      className="flex-shrink-0"
      style={{ 
        width: BRAND_ICON_SIZE, 
        height: BRAND_ICON_SIZE,
        minWidth: BRAND_ICON_SIZE,
        minHeight: BRAND_ICON_SIZE
      }}
    >
      {branding.iconUrl && (
        <img 
          src={`${API_URL}${branding.iconUrl}${cacheBust}`}
          alt={branding.companyName || 'App Icon'}
          loading="eager"
          decoding="sync"
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'contain'
          }}
        />
      )}
    </div>
  );
};

/**
 * Dynamic App Icon Component
 * @param {string} className - CSS class for sizing (e.g., "h-8 w-8")
 */
export const AppIcon = ({ className = "h-8 w-8", size }) => {
  const { branding } = useBranding();
  const _sep = (branding.iconUrl || '').includes('?') ? '&' : '?';
  const cacheBust = branding._ts ? `${_sep}v=${branding._ts}` : '';
  
  // Only show icon if one is uploaded in settings
  if (!branding.iconUrl) {
    return null;
  }
  
  // If size prop is provided, use inline style
  const style = size ? { width: size, height: size } : {};
  
  return (
    <img 
      src={`${API_URL}${branding.iconUrl}${cacheBust}`}
      alt={branding.companyName || 'App Icon'}
      className={`${size ? '' : className} object-contain`}
      loading="eager"
      decoding="sync"
      style={style}
    />
  );
};

/**
 * Dynamic App Logo Component
 * Uses the logo from branding settings for landing page header
 * 
 * @param {string} className - CSS class for sizing
 */
export const AppLogo = ({ className = "h-16 sm:h-20 max-w-[250px]" }) => {
  const { branding } = useBranding();
  const _sep = (branding.logoUrl || '').includes('?') ? '&' : '?';
  const cacheBust = branding._ts ? `${_sep}v=${branding._ts}` : '';
  
  // Only show logo if one is uploaded in settings
  if (!branding.logoUrl) {
    return null;
  }
  
  return (
    <img 
      src={`${API_URL}${branding.logoUrl}${cacheBust}`}
      alt={branding.companyName || 'App Logo'}
      className={`${className} object-contain`}
    />
  );
};

/**
 * Company Name Component
 * Uses the company name from branding settings
 */
export const CompanyName = ({ className = "text-white font-bold text-lg" }) => {
  const { branding } = useBranding();
  
  if (!branding.companyName) {
    return null;
  }
  
  return (
    <span className={className}>{branding.companyName}</span>
  );
};

// Keep EOCLogoIcon for backwards compatibility
export const EOCLogoIcon = ({ className = "h-5 w-5" }) => {
  return <AppIcon className={className} />;
};

export default AppIcon;
